/**
 * ModalHelper — shared open/close/scroll-lock/keyboard-escape mechanics for
 * theme-owned custom modals (ATC modal, size-chart modal). Each modal keeps
 * its own visual styling and content logic; only the lifecycle plumbing is
 * shared here.
 *
 * Dawn's built-in modals (search, newsletter) intentionally do NOT use this
 * helper.
 *
 * Usage:
 *   const helper = new ModalHelper(modalEl, {
 *     overlaySelector: '[data-modal-overlay]',
 *     closeSelector: '[data-modal-close]',
 *     onClose: () => { … },
 *   });
 *   helper.open();   // shows modal, locks body scroll, traps focus
 *   helper.close();  // hides modal, unlocks scroll, releases focus
 *   helper.isOpen(); // → boolean
 */
(function () {
  if (window.ModalHelper) return;

  class ModalHelper {
    constructor(modalEl, opts = {}) {
      this.modal = modalEl;
      this.overlay = modalEl.querySelector(opts.overlaySelector || '[data-modal-overlay]');
      this.closeBtn = modalEl.querySelector(opts.closeSelector || '[data-modal-close]');
      this.focusTarget = opts.focusTarget || null;
      this.onClose = opts.onClose || null;
      this.onKeydown = (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      };
      this.onOverlayClick = () => this.close();
      this.onCloseClick = () => this.close();
      this.bindCloseHandlers();
    }

    open(opts = {}) {
      // Prefer an explicitly passed restore target — e.g. ATC modal calls
      // open() after an async fetch, by which point document.activeElement
      // may have moved off the trigger.
      const explicit = opts.restoreFocusTo instanceof HTMLElement ? opts.restoreFocusTo : null;
      this.lastFocused = explicit || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      // `modal-open` is the new class; `overflow-hidden` is kept in parallel so
      // existing CSS / call-sites that still reference it keep working during
      // the migration.
      document.body.classList.add('modal-open', 'overflow-hidden');
      const target = this.resolveFocusTarget();
      if (target && typeof trapFocus === 'function') trapFocus(target);
    }

    close() {
      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open', 'overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.lastFocused);
      this.lastFocused = null;
      if (this.onClose) this.onClose();
    }

    isOpen() {
      return !this.modal.hasAttribute('hidden');
    }

    resolveFocusTarget() {
      if (typeof this.focusTarget === 'function') return this.focusTarget();
      if (typeof this.focusTarget === 'string') return this.modal.querySelector(this.focusTarget);
      if (this.focusTarget instanceof HTMLElement) return this.focusTarget;
      return null;
    }

    bindCloseHandlers() {
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onCloseClick);
      if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    destroy() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseClick);
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    }
  }

  window.ModalHelper = ModalHelper;
})();
